<template>
  <div
    class="home-container"
    :class="{ 'dashboard-layout': isDashboardRoute }"
    :style="{
      height: containerHeight,
      gridTemplateColumns: gridTemplateColumns,
    }"
  >
    <!-- Left menu -->
    <LeftSidebar ref="leftSidebar" />
    <!-- Center content -->
    <CenterContent ref="centerContent" />
    <!-- Right chat / drawer -->
    <RightChatPanel ref="rightChatPanel" />

    <!-- 对话内 iframe 面板（对话中出现多个 iframe 组件时使用，带右上角 tab 切换） -->
    <div
      v-if="isSmartMode && iframeVisible && iframeItems.length > 0"
      class="iframe-panel"
    >
      <IframeContainer />
    </div>
    <!-- 菜单 iframe 面板（数字人广场/知识库等菜单嵌入页面，独立单 iframe，不混用对话 iframe 组件） -->
    <div v-if="isSmartMode && menuIframeItem" class="iframe-panel">
      <MenuIframe :url="menuIframeItem.url" :name="menuIframeItem.name" />
    </div>
    <div v-if="agentPlazaVisible" class="agent-plaza-panel">
      <AgentPlaza v-if="!agentPlazaIframeUrl" @use="handleAgentPlazaUse" />
      <MenuIframe v-else :url="agentPlazaIframeUrl" :name="agentPlazaIframeName" />
    </div>
  </div>

  <!-- loading placeholder -->
  <!-- <div v-else class="workbench-loading">
    <div class="loading-spinner">
      <i class="el-icon-loading"></i>
      <span>{{ $t('workbench.loading') }}</span>
    </div>
  </div> -->
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import IframeContainer from './components/IframeContainer.vue'
import MenuIframe from './components/MenuIframe.vue'
import { AGENT_ID_MAP, parseAgentTags } from '@/store/modules/cosmoClaw'
import CfoWorkbench from '@/views/workbench/cfo/index.vue'
import AgentPlaza from '@/views/smart-workbench/components/AgentPlaza.vue'
import LeftSidebar from './components/LeftSidebar.vue'
import CenterContent from './components/CenterContent.vue'
import RightChatPanel from './components/RightChatPanel.vue'
import coreMethods from './mixins/coreMethods'
import navigation from './mixins/navigation'
import reportAnalysis from './mixins/reportAnalysis'
import cfoCsoReport from './mixins/cfoCsoReport'
import ceoStrategy from './mixins/ceoStrategy'
import analysisFlow from './mixins/analysisFlow'
import analysisConfigs from './mixins/analysisConfigs'

export default {
  name: 'SmartWorkbench',
  mixins: [
    coreMethods,
    navigation,
    reportAnalysis,
    cfoCsoReport,
    ceoStrategy,
    analysisFlow,
    analysisConfigs
  ],
  components: {
    LeftSidebar,
    CenterContent,
    RightChatPanel,
    IframeContainer,
    MenuIframe,
    AgentPlaza
  },
  data() {
    return {
      sidebarCollapsed: false,
      editingSessionKey: null,
      selectedCardId: '',
      selectedAgent: null,
      skipWorkbenchWatcher: false,
      allAgentsInitialized: false,
      smartFlowCardMessageId: null,
      smartFlowHistory: [],
      smartAnalysisTimers: [],
      lastSmartAnalysisTime: 0,
      selectedDisplayType: '',
      selectedIframeSrc: '',
      clawMode: 'assistant',
      agentChatStarted: false,
      windowWidth: window.innerWidth,
      drawerVisible: false,
      missingIndicatorConfig: null,
      currentComponent: null,
      selectedMenuId: null,
      workbenchTabs: [{ id: 'workbench', title: '工作台', closable: false }],
      activeTabId: 'workbench',
      currentReportId: null,
      currentReportSource: null,
      currentDecisionId: null,
      currentHistoryReportContent: '',
      currentHistoryReportHtml: '',
      sanitizedHistoryReportHtml: '',
      historyReportViewMode: 'web',
      currentHistoryReportId: null,
      currentTaskDetailId: null,
      agentPlazaIframeUrl: '',
      agentPlazaIframeName: '',
      menuIframeItem: null,
      sidebarKey: 0,
      selectedSessionKeys: [],
      mockRestoredIds: [],
      smartInitializationPromise: null,
      smartInitializationVersion: 0,
      digitalPlazaTimer: null,
      smartPlatformReady: false
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    ...mapState('app', ['appDepthMenu']),
    ...mapState('user', ['agentList']),
    ...mapState('cosmoClaw', [
      'sessions',
      'currentSessionKey',
      'messages',
      'iframeVisible',
      'iframeItems',
      'agentPlazaVisible',
      'agentPlazaVisible',
      'centerIframeItem',
      'sessionsLoading',
      'currentAgentId',
      'sessionsLocked'
    ]),
    isSmartMode() {
      // 基于路由判断智擎模式：/dashboard 与 /smart-engine/* 均为智擎模式
      const path = this.$route.path
      if (path.includes('dashboard')) return true
      if (path.startsWith('/smart-engine')) return true
      return false
    },
    isDashboardRoute() {
      return this.$route.path.includes('dashboard')
    },
    // 智擎入口路由（仅 /dashboard，/ 恒为经典工作台）
    isSmartEntryRoute() {
      return this.isDashboardRoute
    },
    containerHeight() {
      return this.isDashboardRoute ? '100vh' : '100%'
    },
    activeMenuIndex() {
      // 当前会话 key 优先（高亮会话记录），其次为选中的数字人/智能体卡片
      return this.currentSessionKey || String(this.selectedCardId || '')
    },
    useDrawer() {
      return this.windowWidth < 1750
    },
    sidebarWidth() {
      if (this.isDashboardRoute) return 220
      if (this.windowWidth >= 1560) return 320
      if (this.windowWidth >= 1440) return 280
      if (this.windowWidth >= 1280) return 250
      return 220
    },
    rightPanelWidth() {
      // if (this.windowWidth >= 1560) return 500
      // if (this.windowWidth >= 1440) return 500
      // if (this.windowWidth >= 1280) return 500
      return 500
    },
    hideSidebar() {
      // 桌面端嵌入时隐藏左侧菜单：通过 query embed=1 控制
      return this.$route.query.embed === '1'
    },
    isPlazaMode() {
      return (
        this.selectedCardId === 'digital_plaza' || this.selectedCardId === 'agent_plaza' || this.selectedCardId === 'knowledge_base'
      )
    },
    // 根据 appDepthMenu 动态生成侧栏菜单，匹配 permission 字段控制可用性
    dynamicMenuGroups() {
      const menu = this.appDepthMenu[0]
      if (!menu || !menu.children || menu.children.length === 0) return []
      const dhNames = new Set(this.digitalHumanList.map((i) => i.agentName))
      const agentNames = new Set(this.myAgentList.map((i) => i.agentName))
      const alwaysEnabled = new Set(['digital_plaza', 'agent_plaza', 'knowledge_base'])

      // 从已启用的数字人/智能体列表中按 internalName 查找完整 item
      const findDhItem = (internalName) => this.digitalHumanList.find((i) => i.agentName === internalName) || null
      const findAgentItem = (internalName) => this.myAgentList.find((i) => i.agentName === internalName) || null

      return menu.children.map((group) => {
        const children = (group.children || []).map((item) => {
          const perm = item.permission || ''
          let matchItem = null
          let clickHandler = ''
          let enabled = false

          if (alwaysEnabled.has(perm)) {
            clickHandler = perm === 'digital_plaza' ? 'handleDigitalPlazaClick'
              : perm === 'agent_plaza' ? 'handleAgentPlazaClick'
                : 'handleKnowledgeBaseClick'
            enabled = true
          } else {
            const permParts = perm.split(',').map((p) => p.trim())
            // 遍历 agentList，按 tags 匹配
            // 优先找完全匹配（无多余修饰符）的 agent；有 GC 等多余修饰符的仅作为兜底
            let fallbackItem = null
            for (const agent of this.agentList || []) {
              if (agent.enabled === false || agent.deleted !== 0) continue
              const rawTags = typeof agent.tags === 'string' ? agent.tags : (Array.isArray(agent.tags) ? agent.tags.join(',') : '')
              if (!rawTags) continue
              const tagList = rawTags.split(',').map((t) => t.trim())
              // permission 的所有部分必须在 agent 的 tags 中都存在
              if (!permParts.every((p) => tagList.includes(p))) continue
              const { type, internalName } = parseAgentTags(agent.tags, agent.name)
              if (!internalName) continue
              // 检查 agent 是否有 GC/YS/LQZ 等修饰符不在 permission 中
              const modifierTags = []
              if (tagList.includes('GC')) modifierTags.push('GC')
              if (tagList.includes('YS')) modifierTags.push('YS')
              if (tagList.includes('LQZ')) modifierTags.push('LQZ')
              const hasExtraModifier = modifierTags.some((m) => !permParts.includes(m))

              if (type === 'digital-human' && dhNames.has(internalName)) {
                matchItem = findDhItem(internalName)
                clickHandler = 'handleDigitalHumanClick'
                enabled = !hasExtraModifier
                if (enabled) break // 找到完全匹配，直接启用
                if (!fallbackItem) fallbackItem = matchItem
              } else if (type === 'agent' && agentNames.has(internalName)) {
                matchItem = findAgentItem(internalName)
                clickHandler = 'handleAgentClick'
                enabled = !hasExtraModifier
                if (enabled) break // 找到完全匹配，直接启用
                if (!fallbackItem) fallbackItem = matchItem
              }
              // hasExtraModifier 时不 break，继续找完全匹配的 agent
            }
            // 循环结束后若未启用但存在带修饰符的兜底项，则置灰（保留 matchItem 供显示）
            if (!enabled && fallbackItem) {
              matchItem = fallbackItem
            }
          }
          return { ...item, _enabled: enabled, _clickHandler: clickHandler, _matchItem: matchItem }
        })
        return { ...group, children }
      })
    },
    isCfoToolTab() {
      return /^tool-/.test(this.activeTabId)
    },
    isBusinessReportTab() {
      return /^(order-forecast-|industry-operation|chain-operation|customer-structure|overseas-chain-revenue)/.test(
        this.activeTabId
      )
    },
    isCfoWorkbench() {
      return this.currentComponent === CfoWorkbench
    },
    gridTemplateColumns() {
      if (!this.isSmartMode) return '1fr'
      const sidebar = this.hideSidebar
        ? '0px'
        : this.sidebarCollapsed
          ? '60px'
          : `${this.sidebarWidth}px`
      const hasIframe = this.iframeVisible && this.iframeItems.length > 0
      const hasMenuIframe = !!this.menuIframeItem
      // 数字人广场/知识库：隐藏右侧栏，留白占满
      if (
        (this.selectedCardId === 'digital_plaza' && (hasIframe || hasMenuIframe)) ||
        (this.selectedCardId === 'knowledge_base' && (hasIframe || hasMenuIframe || this.iframeItems.length > 0)) ||
        (this.selectedCardId === 'agent_plaza' && this.agentPlazaVisible)
      ) {
        return `${sidebar} 1fr`
      }
      // 抽屉模式（桌面端嵌入）：不需要右侧固定列
      if (this.useDrawer) {
        if (this.clawMode === 'digital') {
          return `${sidebar} 0fr 1fr`
        }
        return `${sidebar} 1fr`
      }
      if (this.selectedAgent) {
        return hasIframe ? `${sidebar} 1fr ${this.rightPanelWidth}px` : `${sidebar} 1fr`
      }
      if (this.clawMode === 'digital') {
        return hasIframe ? `${sidebar} 0fr 1fr 1fr` : `${sidebar} 0fr 1fr`
      }
      return `${sidebar} 1fr ${this.rightPanelWidth}px`
    },
    // 动态构建 agent 标签映射
    agentLabels() {
      // 固定短标签映射（用于会话记录 badge 显示）
      const labels = {
        ceo_shu_zi_ren: 'CEO',
        cai_wu_shu_zi_ren: 'CFO',
        gong_ying_lian_shu_zi_ren: 'CSO',
        zhi_biao_ding_yi_zhi_neng_ti: '指标',
        ben_ti_zhi_li_zhi_neng_ti: '本体',
        jing_ying_jue_ce_zhi_neng_ti: '经营'
      }
      return labels
    },
    agentIdToLabel() {
      const map = {}
      for (const [name, id] of Object.entries(AGENT_ID_MAP)) {
        if (this.agentLabels[name]) {
          map[id] = this.agentLabels[name]
        }
      }
      return map
    },
    agentIdToName() {
      const map = {}
      for (const [name, id] of Object.entries(AGENT_ID_MAP)) {
        map[id] = name
      }
      return map
    },
    digitalHumanList() {
      const list = this.agentList || []
      const dynamic = list
        .filter((a) => a.enabled !== false && a.deleted === 0)
        .map((a) => {
          const { type, internalName, isDemo } = parseAgentTags(a.tags, a.name)
          const { isGc, isLqz } = parseAgentTags(a.tags, a.name)
          if (type !== 'digital-human' || !internalName) return null
          return {
            id: a.id,
            title: a.displayName || a.name || '',
            desc: a.description || '',
            logo: a.icon || null,
            icon:
              {
                ceo_shu_zi_ren: 'el-icon-user',
                cai_wu_shu_zi_ren: 'el-icon-coin',
                gong_ying_lian_shu_zi_ren: 'el-icon-shopping-cart-1'
              }[internalName] || 'el-icon-menu',
            status: a.state === 'IDLE' ? '在线' : a.state,
            disabled: false,
            agentName: internalName,
            workspaceId: a.workspaceId,
            isDemo: isDemo || false,
            isGc: isGc || false,
            isLqz: isLqz || false
          }
        })
        .filter(Boolean)
      return dynamic
    },
    myAgentList() {
      const list = this.agentList || []
      const dynamic = list
        .filter((a) => a.enabled !== false && a.deleted === 0)
        .map((a) => {
          const { type, internalName } = parseAgentTags(a.tags, a.name)
          if (type !== 'agent' || !internalName) return null
          return {
            id: a.id,
            title: a.displayName || a.name || '',
            desc: a.description || '',
            logo: a.icon || null,
            icon:
              {
                zhi_biao_ding_yi_zhi_neng_ti: 'el-icon-data-analysis',
                ben_ti_zhi_li_zhi_neng_ti: 'el-icon-film',
                jing_ying_jue_ce_zhi_neng_ti: 'el-icon-set-up'
              }[internalName] || 'el-icon-menu',
            status: a.state === 'IDLE' ? '在线' : a.state,
            disabled: false,
            agentName: internalName,
            workspaceId: a.workspaceId
          }
        })
        .filter(Boolean)
      return dynamic
    },
    // 会话记录不再按 sessionKey 中的用户标识（|!userId）过滤：
    // sessionKey 可能是各种类型（均为 agent:agentId 开头），取出来是哪些就展示哪些。
    filteredSessions() {
      // 仅过滤定时任务类型的会话（agent:agentId:cron_xxx），其余类型正常展示
      return (this.sessions || []).filter(
        (s) => !s.isPending && !/^agent:[^:]+:cron_/.test(String(s.key || ''))
      )
    },
    isAllSessionsSelected: {
      get() {
        const list = this.filteredSessions
        if (list.length === 0) return false
        return list.every((s) => this.selectedSessionKeys.indexOf(s.key) !== -1)
      },
      set(val) {
        // 由 handleToggleSelectAll 处理，这里空实现避免 v-model 双向绑定
      }
    },
    isSomeSessionsSelected() {
      const list = this.filteredSessions
      if (list.length === 0) return false
      const selectedCount = list.filter(
        (s) => this.selectedSessionKeys.indexOf(s.key) !== -1
      ).length
      return selectedCount > 0 && selectedCount < list.length
    },
    agentInfo() {
      if (this.selectedAgent === 'she_bei_guan_li_zhi_neng_ti') {
        return {
          titleKey: 'agentEquipmentTitle',
          descKey: 'agentEquipmentDesc',
          logo: require('@/assets/agentLogo/equipment.png')
        }
      }
      if (this.selectedAgent === 'zhi_liang_guan_kong_zhi_neng_ti') {
        return {
          titleKey: 'agentQualityTitle',
          descKey: 'agentQualityDesc',
          logo: require('@/assets/agentLogo/quality.png')
        }
      }
      return null
    },
    agentCards() {
      if (this.selectedAgent === 'she_bei_guan_li_zhi_neng_ti') {
        return [
          {
            titleKey: 'cardHealthDiagnosis',
            descKey: 'cardHealthDiagnosisDesc',
            content: '帮我诊断设备健康情况[card-action]',
            icon: 'el-icon-first-aid-kit'
          },
          {
            titleKey: 'cardEquipmentInspection',
            descKey: 'cardEquipmentInspectionDesc',
            content: '执行设备巡检任务[card-action]',
            icon: 'el-icon-view'
          },
          {
            titleKey: 'cardReportGeneration',
            descKey: 'cardReportGenerationDesc',
            content: '帮我生成一份运营报告[card-action]',
            icon: 'el-icon-document'
          },
          {
            titleKey: 'cardMaintenanceGuidance',
            descKey: 'cardMaintenanceGuidanceDesc',
            content: '我要进行设备维修，帮我查询维修知识[card-action]',
            icon: 'el-icon-s-tools'
          }
        ]
      }
      if (this.selectedAgent === 'zhi_liang_guan_kong_zhi_neng_ti') {
        return [
          {
            titleKey: 'cardQualityAnalysis',
            descKey: 'cardQualityAnalysisDesc',
            content: '我要创建过程检验任务',
            icon: 'el-icon-warning'
          },
          {
            titleKey: 'cardDefectWarning',
            descKey: 'cardDefectWarningDesc',
            content: '过程检验质量报告查询',
            icon: 'el-icon-bell'
          },
          {
            titleKey: 'cardQualityTrend',
            descKey: 'cardQualityTrendDesc',
            content: '请进行关键特性SPC分析',
            icon: 'el-icon-data-line'
          },
          {
            titleKey: 'cardQualitySuggestion',
            descKey: 'cardQualitySuggestionDesc',
            content: '我要创建8D报告',
            icon: 'el-icon-s-check'
          }
        ]
      }
      return []
    }
  },
  watch: {
    selectedAgent(newVal) {
      if (newVal === false && this.smartPlatformReady) {
        this.loadCurrentWorkbenchComponent()
        // 单独 sessionKey 参数变化（agentId 不变）
        if (!this.$route.query.agentId && this.$route.query.sessionKey) {
          this.$nextTick(() => this._navigateToSession(this.$route.query.sessionKey))
        }
      }
    },
    '$route.path': {
      immediate: true,
      handler() {
        this.stopVoiceInput()
        // 从其他页面切回智擎入口页时，可能需要补一次初始化，
        // 避免一直停留在加载态
        if (this.isSmartMode && this.isSmartEntryRoute && !this.smartPlatformReady) {
          this.initializeSmartWorkbench()
        }
        if (this.isSmartMode && this.smartPlatformReady) {
          this.loadCurrentWorkbenchComponent()
        }
      }
    },
    currentSessionKey() {
      this.stopVoiceInput()
      var self = this
      setTimeout(function () {
        self._restoreMockHistoryMessages().catch(function (e) {
          console.error('[MOCK RESTORE] Error in session restore:', e)
        })
      }, 800)
    },
    '$route.query': {
      immediate: true,
      handler(query) {
        this.stopVoiceInput()
        // URL 中有 agentId：导航到对应智能体
        if (query.agentId) {
          // 等初始化完成后再执行导航
          if (this.smartPlatformReady && this.isSmartEntryRoute) {
            this.$nextTick(() => {
              this.navigateToAgent(query.agentId)
              if (query.sessionKey) {
                this._navigateToSession(query.sessionKey)
              }
            })
          }
          return
        }
        // 没有 agentId 且已就绪时跳默认入口（第一个数字人）
        if (!this.isSmartMode || !this.isSmartEntryRoute || !this.smartPlatformReady) {
          return
        }
        this.$nextTick(() => {
          const entry = this._getDefaultEntry()
          if (entry.type === 'digital') {
            this.handleDigitalHumanClick(entry.item)
          } else if (entry.type === 'agent') {
            this.handleAgentClick(entry.item)
          } else {
            this.handleDigitalPlazaClick()
          }
        })
      }
    },
    messages(msgs, prevMsgs) {
      if (this.selectedAgent && !this.agentChatStarted && msgs.length > 0) {
        this.agentChatStarted = true
      }
      if (msgs.length === 0 && this.iframeVisible) {
        this.$store.commit('cosmoClaw/SET_IFRAME_VISIBLE', false)
        this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
        this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', [])
      }
      if (msgs.length === 0 && this.centerIframeItem) {
        this.$store.commit('cosmoClaw/SET_CENTER_IFRAME', null)
      }
      // drawer 模式下，浮窗发消息后自动打开抽屉
      if (this.useDrawer && !this.drawerVisible && msgs.length > 0) {
        const last = msgs[msgs.length - 1]
        if (last && last.type === 'user') {
          this.drawerVisible = true
          this.$nextTick(() => {
            const drawerComp = this.$refs && this.$refs.rightChatPanel && this.$refs.rightChatPanel.$refs.drawer
            if (drawerComp) {
              drawerComp.rendered = true
              drawerComp.$forceUpdate()
            }
          })
        }
      }
      // 双保险：消息列表更新后，检查是否需要恢复 mock 历史消息
      // 防止 loadHistory 完成晚于 currentSessionKey watcher 触发导致恢复失败
      if (msgs.length > 0 && prevMsgs && prevMsgs.length === 0) {
        var self = this
        this.$nextTick(function () {
          self._restoreMockHistoryMessages().catch(function (e) {
            console.error('[MOCK RESTORE] Error in messages watcher:', e)
          })
        })
      }
    },
    iframeVisible(val) {
      if (val && !this.selectedAgent && !['digital_plaza', 'agent_plaza', 'knowledge_base'].includes(this.selectedCardId)) {
        this.clawMode = 'digital'
      }
    },
    clawMode(val) {
      if (val === 'assistant' && this.iframeVisible && !['digital_plaza', 'agent_plaza', 'knowledge_base'].includes(this.selectedCardId)) {
        this.$store.commit('cosmoClaw/SET_IFRAME_VISIBLE', false)
        this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
        this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
        this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', [])
      }
    },
    isSmartMode(val) {
      // 基于路由的智擎模式切换：进入智擎路由时初始化，离开时断开 WS
      this.stopVoiceInput()
      this.$store.commit('cosmoClaw/SET_LOCAL_DEMO_MODE', false)
      this.selectedAgent = null
      this.selectedCardId = ''
      this.selectedDisplayType = ''
      this.selectedIframeSrc = ''
      this.clawMode = 'assistant'
      this.agentChatStarted = false
      this.drawerVisible = false
      this.agentPlazaIframeUrl = ''
      this.agentPlazaIframeName = ''
      this.menuIframeItem = null
      this.$store.commit('cosmoClaw/SET_IFRAME_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', [])
      this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', true)
      this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', [])
      this.$store.commit('cosmoClaw/SET_CENTER_IFRAME', null)
      this.$store.commit('cosmoClaw/SET_MESSAGES', [])
      this.$store.commit('cosmoClaw/SET_DRAWER_VISIBLE', false)
      this.$store.commit('cosmoClaw/SET_DRAWER_CONFIG', {})
      if (val) {
        this.initializeSmartWorkbench()
      } else {
        this.selectedMenuId = null
        this.allAgentsInitialized = false
        this.smartPlatformReady = false
        this.smartInitializationVersion += 1
        this.smartInitializationPromise = null
        this.clearDigitalPlazaTimer()
        this.$store.dispatch('cosmoClaw/disconnect')
      }
    },
    centerIframeItem(item) {
      if (item && item.url) {
        const exists = this.workbenchTabs.find((t) => t.id === 'center-iframe')
        if (!exists) {
          this.workbenchTabs.push({
            id: 'center-iframe',
            title: item.name || '看板',
            closable: true
          })
        } else {
          exists.title = item.name || '看板'
        }
        this.activeTabId = 'center-iframe'
      } else {
        const idx = this.workbenchTabs.findIndex((t) => t.id === 'center-iframe')
        if (idx !== -1) {
          this.workbenchTabs.splice(idx, 1)
          if (this.activeTabId === 'center-iframe') {
            this.activeTabId =
              this.workbenchTabs[this.workbenchTabs.length - 1]?.id || 'workbench'
          }
        }
      }
    },
    dynamicMenuGroups: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          const menu = this.$refs && this.$refs.leftSidebar && this.$refs.leftSidebar.$refs.dashboardMenu
          if (menu) {
            if (typeof menu.initMenuItems === 'function') menu.initMenuItems()
            const activeVal = this._resolveMenuIndex(this.selectedCardId) || this.currentSessionKey || ''
            if (activeVal) menu.activeIndex = activeVal
          }
        })
      }
    },
    selectedCardId() {
      // 初始化默认数字人/智能体时同步 dashboard 菜单高亮
      this.$nextTick(() => {
        const menu = this.$refs && this.$refs.leftSidebar && this.$refs.leftSidebar.$refs.dashboardMenu
        if (menu && this.selectedCardId) {
          const activeVal = this._resolveMenuIndex(this.selectedCardId)
          if (activeVal) menu.activeIndex = activeVal
        }
      })
    },
    useDrawer(val, oldVal) {
      if (oldVal === true && val === false) {
        this.sidebarKey++
      }
    }
  },
  mounted() {
    this.calcHeight()
    window.addEventListener('resize', this.calcHeight)
    window.addEventListener('beforeunload', this._handleBeforeUnload)
    document.addEventListener('md-card-action', this.handleSmartCardAction)
    document.addEventListener('click', this.handleSmartHtmlAction)
    document.addEventListener('click', this.handleCeoStrategyFollowUpAction)
    document.addEventListener('click', this.handleSmartToggleDetail)
    document.addEventListener('cosmo-claw-smart-action', this.handleSmartActionEvent)
    document.addEventListener(
      'cosmo-claw-local-demo-message',
      this.handleLocalDemoMessage
    )
    document.addEventListener('cosmo-claw-mock-trigger', this.handleMockTrigger)
    document.addEventListener('cosmo-claw-open-preference', this.handleOpenPreference)
    window.triggerCeoFollowUp = this.handleCeoFollowUp.bind(this)
    window.startPVMCMAnalysis = this.startPVMCMAnalysis.bind(this)
    window.showPVMCSummary = this.showPVMCSummary.bind(this)
    window.generateFourQuadrantPlans = this.generateFourQuadrantPlans.bind(this)
    window.applyPlanA = this.applyPlanA.bind(this)
    window.applyPlanB = this.applyPlanB.bind(this)
    window.applyPlanC = this.applyPlanC.bind(this)
    window.openDrawer = () => {
      this.drawerVisible = true
    }
    window.applyPlanD = this.applyPlanD.bind(this)
    window.applyIndicator = this.applyIndicator.bind(this)
    window.applyRecommendedIndicators = this.applyRecommendedIndicators.bind(this)
    window.applyAllIndicators = this.applyAllIndicators.bind(this)
    window.createIndicator = this.createIndicator.bind(this)
    window.generateMissingIndicators = this.generateMissingIndicators.bind(this)
    window.startBatchCreateIndicators = this.startBatchCreateIndicators.bind(this)
    window.saveSkillToLibrary = this.saveSkillToLibrary.bind(this)
    window.triggerCfoMarginFlow = this.triggerCfoMarginFlow.bind(this)
    window.triggerCfoScenario = this.triggerCfoScenario.bind(this)
    window.triggerCeoH1Summary = this.triggerCeoH1Summary.bind(this)
    window.triggerCfoMarginAnalysis = this.renderCfoMarginAnalysisCard.bind(this)
    window.cancelCfoMarginAnalysis = this.cancelCfoMarginAnalysis.bind(this)
    window.csoStepBomDiagnosis = this.csoStepBomDiagnosis.bind(this)
    window.csoStepBomNextStep = this.csoStepBomNextStep.bind(this)
    window.csoStepConfirmPricePlan = this.csoStepConfirmPricePlan.bind(this)
    window.csoStepInternalAbsorb = this.csoStepInternalAbsorb.bind(this)
    window.csoSelectPlan = this.csoSelectPlan.bind(this)
    window.csoSubmitToCeo = this.csoSubmitToCeo.bind(this)
    window.csoShowDecisionDetail = this.csoShowDecisionDetail.bind(this)
    window.csoShowInventory = this.csoShowInventory.bind(this)
    window.cfoStepDeepAnalysis = this.cfoStepDeepAnalysis.bind(this)
    window.cfoStepReport = this.cfoStepReport.bind(this)
    window.openE2EReport = this.openE2EReport.bind(this)
    window.openCfoReportTab = this.openCfoReportTab.bind(this)
    window.runChainBreakdown = this.runChainBreakdown.bind(this)
    window.triggerChainPerformanceBreakdown = this.triggerChainPerformanceBreakdown.bind(
      this
    )
    window.triggerIndustryCompetitiveness = this.triggerIndustryCompetitiveness.bind(
      this
    )
    window.triggerIndustryCompetitivenessConfirm = this.triggerIndustryCompetitivenessConfirm.bind(
      this
    )
    window.openIndustryReport = this.openIndustryReport.bind(this)
    window.openChainReport = this.openChainReport.bind(this)
    window.runCustomerStructure = this.runCustomerStructure.bind(this)
    window.triggerCeoStrategyPlanningDemo = this.triggerCeoStrategyPlanningDemo.bind(
      this
    )
    window.renderCeoCurveReport = (curveKey) => {
      if (curveKey === 'curve1') this.renderCeoCurve1Report()
      else if (curveKey === 'curve2') this.renderCeoCurve2Report()
      else if (curveKey === 'curve3') this.renderCeoCurve3Report()
    }
    this.loadCurrentWorkbenchComponent()
    if (this.isSmartMode) {
      console.log(this.appDepthMenu, 'xxxxxxxmenu')
      this.initializeSmartWorkbench()
      window.testMockHistory = function (scenario) {
        var s = scenario || 'ceo-h1-summary'
        var msg = {
          id: 'test-mock-' + Date.now(),
          type: 'assistant',
          content: '[mock:' + s + ']',
          timestamp: Date.now(),
          isHistory: true,
          richHtml: false
        }
        this.$store.commit('cosmoClaw/ADD_MESSAGE', msg)
        setTimeout(
          function () {
            this._restoreMockHistoryMessages()
          }.bind(this),
          800
        )
      }.bind(this)
    }
  },
  beforeDestroy() {
    this._disposeProfitMarginCharts && this._disposeProfitMarginCharts()
    this.stopVoiceInput()
    window.removeEventListener('resize', this.calcHeight)
    window.removeEventListener('beforeunload', this._handleBeforeUnload)
    document.removeEventListener('md-card-action', this.handleSmartCardAction)
    document.removeEventListener('click', this.handleSmartHtmlAction)
    document.removeEventListener('click', this.handleCeoStrategyFollowUpAction)
    document.removeEventListener('click', this.handleSmartToggleDetail)
    document.removeEventListener('cosmo-claw-smart-action', this.handleSmartActionEvent)
    document.removeEventListener(
      'cosmo-claw-local-demo-message',
      this.handleLocalDemoMessage
    )
    document.removeEventListener('cosmo-claw-mock-trigger', this.handleMockTrigger)
    document.removeEventListener('cosmo-claw-open-preference', this.handleOpenPreference)
    this.clearSmartAnalysisTimers()
    this.$store.commit('cosmoClaw/SET_IFRAME_VISIBLE', false)
    this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
    this.$store.commit('cosmoClaw/SET_AGENT_PLAZA_VISIBLE', false)
    this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', [])
    this.$store.dispatch('cosmoClaw/abortConversation')
    this.$store.dispatch('cosmoClaw/disconnect')
  }
}
</script>

<style lang="scss" scoped>
.flex-1 {
  flex: 1;
  overflow: hidden;
}

.home-container {
  position: relative;
  padding: 16px;
  display: grid;
  overflow: hidden !important;
  gap: 0;
  transition: grid-template-columns 0.4s ease;
  background: url("~@/assets/imgs/workbench/background.png") no-repeat center center /
    cover;
  background-size: 100% auto;
}

/* ========== Dashboard 路由覆盖样式 ========== */
.home-container.dashboard-layout {
  padding: 8px 7px;
  ::v-deep .left-sidebar {
    background: transparent;
    margin-right: 0px;
    border-radius: 8px;
    // padding-right: 10px;
  }
  ::v-deep .center-scroll {
    border-radius: 8px 0 0 8px;
  }
  ::v-deep .agent-view {
    margin-left: 0;
    justify-content: flex-start;
  }
  ::v-deep .sidebar-header {
    display: none;
  }
  ::v-deep .dashboard-menu {
    padding: 0;
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
  }
  ::v-deep .sidebar-section {
    padding: 20px 0 0 8px;
  }
  ::v-deep .session-title {
    font-size: 12px;
  }
  ::v-deep .dashboard-el-menu {
    border-right: none;
    background: transparent;
    padding-bottom: 10px;
    border-bottom: 1px solid #F7F8FC;
  }
  ::v-deep .el-menu-item.is-disabled {
    opacity: 0.50;
  }
  ::v-deep .dashboard-el-menu .el-submenu__title,
  ::v-deep .dashboard-el-menu .el-menu-item {
    height: 34px !important;
    line-height: 32px !important;
    padding: 0 8px !important;
    font-size: 12px;
    color: #333;
    border-radius: 4px;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    margin: 1px 6px;
    width: auto;
    min-width: 0;
  }
  ::v-deep .dashboard-el-menu .el-menu--inline .el-menu-item:hover {
    background: rgba(156, 167, 248, 0.2) !important;
  }
  ::v-deep .dashboard-el-menu .el-submenu__title:hover {
    background: rgba(156, 167, 248, 0.2) !important;
  }
  ::v-deep .dashboard-el-menu .el-submenu__title i,
  ::v-deep .dashboard-el-menu .el-menu-item i {
    font-size: 14px;
    margin-right: 6px;
  }
  ::v-deep .dashboard-el-menu .el-menu-item.is-active,
  ::v-deep .dashboard-el-menu .el-menu-item.session-active {
    background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%) !important;
    color: #fff !important;
  }
  ::v-deep .dashboard-el-menu .el-menu-item.is-active:hover,
  ::v-deep .dashboard-el-menu .el-menu-item.session-active:hover {
    background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%) !important;
    color: #fff !important;
  }
  ::v-deep .dashboard-el-menu .el-menu-item:hover {
    background: rgba(156, 167, 248, 0.2) !important;
  }
  ::v-deep .dashboard-el-menu .session-more {
    flex-shrink: 0;
    font-size: 14px;
    color: #8f959e;
    opacity: 0;
    transition: opacity 0.2s;
  }
  ::v-deep .dashboard-el-menu .el-menu-item:hover .session-more,
  ::v-deep .dashboard-el-menu .el-submenu__title:hover .session-more {
    opacity: 1;
  }
  ::v-deep .dashboard-el-menu .el-submenu .el-menu {
    background: transparent;
  }
  ::v-deep .dashboard-el-menu .el-submenu .el-menu--inline {
    border-left: 1px dashed #aaaaaa !important;
    margin-left: 25px !important;
  }
  ::v-deep .dashboard-el-menu .el-submenu .el-menu--inline .el-menu-item {
    padding-left: 8px !important;
  }
  ::v-deep .dashboard-el-menu .el-submenu .el-menu .el-menu-item .session-agent-badge {
    font-size: 10px;
    color: #4a5ff3;
    background: #eef1fd;
    padding: 0 5px;
    border-radius: 3px;
    font-weight: 500;
    line-height: 18px;
    margin-right: 4px;
    flex-shrink: 0;
  }
  ::v-deep .dashboard-el-menu .el-menu-item .session-title,
  ::v-deep .dashboard-el-menu .el-submenu__title .session-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
/* ========== iframe面板 ========== */
.agent-plaza-panel {
  margin-left: 10px;
  flex: 1;
  min-width: 0;
  border: 1px solid #ffffff;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  overflow: hidden;
}

.iframe-panel {
  margin-left: 10px;
  border: 1px solid #ffffff;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.workbench-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 48px);

  .loading-spinner {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    color: #8f959e;

    i {
      font-size: 24px;
    }
  }
}

</style>
